// Team codes: a squad of five unit types as one line of text, made by the team editor and pasted
// into a game ("ctf-team1:" + base64 of compact JSON). decodeTeam checks everything the rules rely on.

import type { TraitId, UnitType } from './types';
import { TRAITS, SQUAD_SIZE, TRAIT_CLASHES } from './units';

export const TEAM_CODE_PREFIX = 'ctf-team1:';
export const MOVE_RANGE = [1, 9] as const;
export const ZONE_REACH = 3;

export interface Team { name: string; squad: UnitType[] }

// Compact form: [name, [[unit name, letter, move, [q, r, q, r, ...], [trait, ...]], ...]].
type Packed = [string, [string, string, number, number[], string[]][]];

const toBase64 = (text: string) => {
  const bytes = new TextEncoder().encode(text);
  let bin = '';
  bytes.forEach(b => { bin += String.fromCharCode(b); });
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
const fromBase64 = (code: string) => {
  const bin = atob(code.replace(/-/g, '+').replace(/_/g, '/'));
  return new TextDecoder().decode(Uint8Array.from(bin, c => c.charCodeAt(0)));
};

export function encodeTeam(team: Team): string {
  const packed: Packed = [team.name, team.squad.map(u => [u.name, u.letter, u.move, u.zone.flat(), [...u.traits]])];
  return TEAM_CODE_PREFIX + toBase64(JSON.stringify(packed));
}

const hexDistance = (q: number, r: number) => (Math.abs(q) + Math.abs(r) + Math.abs(q + r)) / 2;

// Problems with a squad that would break the rules (empty when it can be played).
export function squadErrors(squad: UnitType[]): string[] {
  const errs: string[] = [];
  if (squad.length !== SQUAD_SIZE) errs.push(`A squad has ${SQUAD_SIZE} units (this one has ${squad.length}).`);
  squad.forEach((u, i) => {
    const who = `Slot ${i + 1}${u?.name ? ` (${u.name})` : ''}`;
    if (!u || typeof u.name !== 'string' || !u.name.trim()) { errs.push(`${who}: needs a name.`); return; }
    if (!Number.isInteger(u.move) || u.move < MOVE_RANGE[0] || u.move > MOVE_RANGE[1]) errs.push(`${who}: Move must be ${MOVE_RANGE[0]}–${MOVE_RANGE[1]}.`);
    if (!Array.isArray(u.zone)) { errs.push(`${who}: zone is missing.`); return; }
    const seen = new Set<string>();
    for (const o of u.zone) {
      const [q, r] = o;
      const d = hexDistance(q, r);
      if (!Number.isInteger(q) || !Number.isInteger(r) || d < 1 || d > ZONE_REACH) errs.push(`${who}: zone hexes must be 1–${ZONE_REACH} away.`);
      if (seen.has(`${q},${r}`)) errs.push(`${who}: a zone hex is listed twice.`);
      seen.add(`${q},${r}`);
    }
    for (const t of u.traits) if (!(t in TRAITS)) errs.push(`${who}: unknown trait "${t}".`);
    for (const [a, b] of TRAIT_CLASHES) {
      if (u.traits.includes(a) && u.traits.includes(b)) errs.push(`${who}: ${TRAITS[a].name} and ${TRAITS[b].name} can't go together.`);
    }
  });
  return errs;
}

// Reads a team code; throws an Error saying what is wrong.
export function decodeTeam(code: string): Team {
  const text = code.trim();
  if (!text.startsWith(TEAM_CODE_PREFIX)) throw new Error(`Not a team code (it should start with "${TEAM_CODE_PREFIX}").`);
  let packed: Packed;
  try { packed = JSON.parse(fromBase64(text.slice(TEAM_CODE_PREFIX.length))); } catch { throw new Error('The team code is damaged: copy it again in full.'); }
  if (!Array.isArray(packed) || typeof packed[0] !== 'string' || !Array.isArray(packed[1])) throw new Error('The team code is damaged: copy it again in full.');
  const squad: UnitType[] = packed[1].map(([name, letter, move, flat, traits]) => {
    const zone: [number, number][] = [];
    for (let i = 0; i + 1 < (flat ?? []).length; i += 2) zone.push([flat[i], flat[i + 1]]);
    return { name: String(name ?? ''), letter: String(letter ?? '').slice(0, 2), move: Number(move), zone, traits: (traits ?? []) as TraitId[] };
  });
  const errs = squadErrors(squad);
  if (errs.length) throw new Error(errs.join(' '));
  return { name: packed[0], squad };
}
